// Écran d'accueil, repris de Safecheck-sinistre : l'écran entier est fait des deux grandes fenêtres
// côte à côte. ASSURANCES mène à la connexion de ce projet, ENTREPRISES à l'application Entreprises.
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, MotionConfig, type Transition } from "framer-motion";
import type { IconType } from "react-icons";
import { FiArrowUpRight, FiBriefcase, FiExternalLink, FiLock, FiShield } from "react-icons/fi";
import "./Fenetres.css";
import "./AccueilPage.css";

const URL_ENTREPRISES = "https://app.safecheckrdc.com/";

// Ressorts à amortissement critique : rapides, sans rebond.
const RESSORT: Transition = { type: "spring", stiffness: 170, damping: 26 };
const RESSORT_VIF: Transition = { type: "spring", stiffness: 320, damping: 30 };
const RESSORT_LOURD: Transition = { type: "spring", stiffness: 120, damping: 24 };
const COURBE = [0.7, 0, 0.2, 1] as const;

type Porte = {
  cle: "assurances" | "entreprises";
  indice: string;
  cible: string;
  titre: string;
  mention: string;
  texte: string;
  note: string;
  image: string;
  cadrage: string;
  Icone: IconType;
  NoteIcone: IconType;
};

const PORTES: Porte[] = [
  {
    cle: "assurances",
    indice: "01",
    cible: "Sociétés d'assurance",
    titre: "Assurances",
    mention: "Gestion confiée",
    texte: "Factures d'hôpitaux contrôlées, plafonds suivis, portefeuille en temps réel.",
    note: "Connexion sécurisée",
    image: "/login-assurances.jpg",
    cadrage: "32% 30%",
    Icone: FiShield,
    NoteIcone: FiLock,
  },
  {
    cle: "entreprises",
    indice: "02",
    cible: "Employeurs",
    titre: "Entreprises",
    mention: "Gestion directe",
    texte: "La couverture santé de vos équipes, gérée directement avec Safecheck RDC.",
    note: "Application Entreprises",
    image: "/accueil-entreprises.jpg",
    cadrage: "60% 40%",
    Icone: FiBriefcase,
    NoteIcone: FiExternalLink,
  },
];

function Fenetre({
  porte,
  rang,
  href,
  actif,
  autreActif,
  surActif,
}: {
  porte: Porte;
  rang: number;
  href: string;
  actif: boolean;
  autreActif: boolean;
  surActif: (v: boolean) => void;
}) {
  const depart = 0.15 + rang * 0.14;
  const { Icone, NoteIcone } = porte;
  const contenu = (
    <>
      <span className="sc-porte-media" aria-hidden="true">
        {/* Zoom au survol (extérieur) et lente respiration de la photo (intérieur). */}
        <motion.span className="sc-porte-zoom" animate={{ scale: actif ? 1.07 : 1 }} transition={RESSORT_LOURD}>
          <motion.img
            src={porte.image}
            alt=""
            decoding="async"
            fetchPriority="high"
            style={{ objectPosition: porte.cadrage }}
            initial={{ scale: 1.18 }}
            animate={{ scale: [1.18, 1, 1.04] }}
            transition={{ duration: 18, times: [0, 0.1, 1], ease: "easeInOut", repeat: Infinity, repeatType: "mirror", delay: depart }}
          />
        </motion.span>
        <motion.span className="sc-porte-voile" animate={{ opacity: actif ? 0.88 : 1 }} transition={RESSORT} />
        {/* Rideau qui se lève à l'arrivée. */}
        <motion.span className="sc-porte-rideau" initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 0.85, ease: COURBE, delay: depart }} />
      </span>

      <span className="sc-porte-corps">
        <motion.span className="sc-porte-surtitre" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ ...RESSORT, delay: depart + 0.55 }}>
          <span className="sc-porte-pastille" aria-hidden="true">
            <Icone />
          </span>
          <span className="sc-porte-indice">{porte.indice}</span>
          <span className="sc-porte-filet" aria-hidden="true" />
          {porte.cible}
        </motion.span>

        <span className="sc-porte-masque">
          <motion.span
            className="sc-porte-titre"
            initial={{ y: "108%" }}
            animate={{ y: 0 }}
            transition={{ ...RESSORT_LOURD, delay: depart + 0.45 }}
          >
            {porte.titre}
          </motion.span>
        </span>

        <motion.span className="sc-porte-texte" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...RESSORT, delay: depart + 0.7 }}>
          {porte.texte}
        </motion.span>

        <motion.span className="sc-porte-acces" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...RESSORT, delay: depart + 0.82 }}>
          Accéder à l&apos;espace
          <motion.span className="sc-porte-fleche" aria-hidden="true" animate={{ rotate: actif ? 45 : 0, scale: actif ? 1.08 : 1 }} transition={RESSORT_VIF}>
            <FiArrowUpRight />
          </motion.span>
        </motion.span>

        <motion.span className="sc-porte-pied" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: depart + 1 }}>
          <motion.span className="sc-porte-ligne" aria-hidden="true" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, ease: COURBE, delay: depart + 0.9 }} />
          <span className="sc-porte-mention">{porte.mention}</span>
          <span className="sc-porte-note">
            <NoteIcone aria-hidden="true" /> {porte.note}
          </span>
        </motion.span>
      </span>
    </>
  );

  const evenements = {
    onMouseEnter: () => surActif(true),
    onMouseLeave: () => surActif(false),
    onFocus: () => surActif(true),
    onBlur: () => surActif(false),
  };
  const classe = `sc-porte sc-porte-${porte.cle}`;
  return (
    // Le panneau survolé s'élargit, l'autre se resserre.
    <motion.div className="sc-porte-cadre" animate={{ flexGrow: actif ? 1.35 : autreActif ? 0.78 : 1 }} transition={RESSORT}>
      {porte.cle === "entreprises" ? (
        <a href={href} className={classe} {...evenements}>
          {contenu}
        </a>
      ) : (
        <Link to={href} className={classe} {...evenements}>
          {contenu}
        </Link>
      )}
      {rang === 0 && (
        <motion.span
          className="sc-accueil-sceau"
          aria-hidden="true"
          initial={{ scale: 0, rotate: -40 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ ...RESSORT_LOURD, delay: 1.15 }}
        >
          <img src="/bouclier.png" alt="" width={56} height={56} className="sc-logo" />
        </motion.span>
      )}
    </motion.div>
  );
}

export function AccueilPage() {
  const [actif, setActif] = useState<number | null>(null);
  return (
    // reducedMotion="user" : animations coupées si le système demande moins de mouvement.
    <MotionConfig reducedMotion="user">
      <main id="contenu" className="sc-accueil">
        <motion.header className="sc-accueil-barre" initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...RESSORT, delay: 0.9 }}>
          <h1 className="sc-accueil-marque">
            <img src="/logo-blanc.png" alt="Safecheck RDC" width={291} height={96} fetchPriority="high" className="sc-logo" />
            <span className="visually-hidden"> Safecheck app</span>
          </h1>
          <p className="sc-accueil-devise">
            Safecheck RDC <span aria-hidden="true">·</span> Gestion des sinistres santé
          </p>
        </motion.header>

        <div className="sc-portes">
          {PORTES.map((porte, i) => (
            <Fenetre
              key={porte.cle}
              porte={porte}
              rang={i}
              href={porte.cle === "assurances" ? "/login" : URL_ENTREPRISES}
              actif={actif === i}
              autreActif={actif !== null && actif !== i}
              surActif={(v) => setActif(v ? i : null)}
            />
          ))}
        </div>
      </main>
    </MotionConfig>
  );
}
